/**
 * Parse Clients page deep-link:
 *   `/accounts?client=<masterAccountId>&panel=support[&case=KK-######]`
 *
 * `case` (staff Support notification emails) opens the EXACT Support Case
 * inside the Support panel. It is presentation only — the Case is rendered
 * only if the authorized Support list for that Client contains it.
 */

export type AccountsDeepLink = {
  clientId: string | null;
  panel: "support" | null;
  /** Normalized immutable Case number (KK-######) or null. */
  caseId: string | null;
};

const CASE_ID_RE = /^KK-\d{6}$/;

export function normalizeDeepLinkCaseId(raw: string | null | undefined): string | null {
  const v = String(raw || "").trim().toUpperCase();
  if (!v) return null;
  const withPrefix = v.startsWith("KK-") ? v : `KK-${v}`;
  return CASE_ID_RE.test(withPrefix) ? withPrefix : null;
}

export function parseAccountsDeepLink(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): AccountsDeepLink {
  const clientRaw = String(searchParams.get("client") || "").trim();
  const panelRaw = String(searchParams.get("panel") || "")
    .trim()
    .toLowerCase();
  const caseId = normalizeDeepLinkCaseId(searchParams.get("case"));
  return {
    clientId: clientRaw || null,
    // An exact Case link implies the Support panel.
    panel: panelRaw === "support" || caseId ? "support" : null,
    caseId,
  };
}

/** Keys stripped when dismissing Client Details opened via deep-link. */
export const ACCOUNTS_DEEP_LINK_KEYS = ["client", "panel", "case"] as const;

/**
 * Remove deep-link query keys so closing Client Details cannot immediately
 * re-open from leftover `/accounts?client=…&panel=support&case=…` params.
 */
export function stripAccountsDeepLinkParams(
  searchParams: URLSearchParams,
): { next: URLSearchParams; changed: boolean } {
  const next = new URLSearchParams(searchParams);
  let changed = false;
  for (const key of ACCOUNTS_DEEP_LINK_KEYS) {
    if (next.has(key)) {
      next.delete(key);
      changed = true;
    }
  }
  return { next, changed };
}
